import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { UntypedFormBuilder } from '@angular/forms'
import { Subscription } from 'rxjs'
import { finalize, switchMap } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { AccountService } from '../../services/account.service'
import { Tag, TagList } from 'src/utils/types'

@Component({
  selector: 'account-tags',
  templateUrl: './account-tags.component.html',
  styleUrls: ['./account-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountTagsComponent implements OnInit {
  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiService: ApiService,
    public uiService: UiService,
    private accountService: AccountService
  ) {}

  private settingsSubscription: Subscription = null
  private updateSubscription: Subscription = null
  private submitted = false
  isLoading = true

  tagSettingsFormModel = this.formBuilder.group({
    resourceTags: [new Array<Tag>()],
  })

  get loading() {
    return this.isLoading
  }

  get updateButtonDisabled() {
    return !(this.tagSettingsFormModel.dirty && this.tagSettingsFormModel.valid)
  }

  ngOnInit() {
    // load the subscription settings
    this.settingsSubscription = this.accountService.accountIdStream
      .pipe(
        switchMap((_accountId) => this.accountService.resourceAttributes$),
        finalize(() => (this.settingsSubscription = null))
      )
      .subscribe(
        (resourceAttributes) => {
          this.initializeTags(resourceAttributes.tags)
          this.isLoading = false
        },
        (error) => {
          this.isLoading = false
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        }
      )
  }

  ngOnDestroy() {
    if (this.settingsSubscription) {
      this.settingsSubscription.unsubscribe()
      this.settingsSubscription = null
    }
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe()
      this.updateSubscription = null
    }
  }

  private initializeTags(tags: { [key: string]: string }) {
    let tagList = Object.keys(tags).reduce((tl, tagKey) => {
      tl.push({ tagName: tagKey, tagValue: tags[tagKey] })
      return tl
    }, [] as TagList)
    this.tagSettingsFormModel.setValue({
      resourceTags: tagList,
    })
  }

  updateResourceTags() {
    // Reduce the TagList into a simple dictionary of key:value pairs.
    // Converts null values to empty strings.
    let tags = this.tagSettingsFormModel.value.resourceTags.reduce((accum, val) => {
      accum[val.tagName] = val.tagValue || ''
      return accum
    }, {})
    this.updateSubscription = this.accountService
      .setResourceAttributes$(tags)
      .pipe(finalize(() => (this.updateSubscription = null)))
      .subscribe(
        () => {
          this.submitted = true
          this.tagSettingsFormModel.markAsPristine()
        },
        handleArpioError(window.location.href, this.apiService, this.uiService)
      )
  }

  get submitting() {
    return subscriptionActive(this.updateSubscription)
  }

  get doneSubmitting() {
    return this.submitted && !this.tagSettingsFormModel.dirty
  }
}
